// What a seed's download holds, as stored: the master and the audio its
// clips play. Pure — the page turns keys into URLs, and renders the tracks
// (lib/seed-stems.ts) for the zip's `arrangement` folder.
//
//   <title>/<title> (mix).wav
//   <title>/arrangement/<track>.wav   every track whole, bar 1 to the end
//   <title>/clips/<track> - <clip>.wav
//
// A track's files are its DISTINCT objects: a loop placed eight times is one
// file, and a lane of five different takes is five. Names come from the
// clips (Live's "(Freeze)" and friends cleaned up), falling back to the
// track's.

import type { SeedApiDetail } from "@shared/api-types.js";
import type { Snapshot } from "@shared/seed-history.js";
import { cleanClipName } from "@/lib/track-name";
import { extOfKey, safeFilename, uniquePaths, type DownloadFile } from "@/lib/download";

export interface SeedDownloadTrack {
  /** The id the seed page's track list gives this lane (`PlayerTrack.id`). */
  id: string;
  name: string;
  files: Array<{ name: string; key: string }>;
}

export interface SeedDownloadPlan {
  title: string;
  masterKey: string | null;
  tracks: SeedDownloadTrack[];
  /** A seed from before per-clip audio: its files are one full-length stem
   * per track, so they ARE the arrangement and there are no clips. */
  stemShaped: boolean;
}

interface ClipLike {
  name?: string | null;
  audioKey?: string | null;
}
interface LaneLike {
  name: string;
  arrangementClips?: ClipLike[];
  sessionClips?: ClipLike[];
}

export function seedDownloadPlan(seed: Pick<SeedApiDetail, "id" | "title" | "arrangement" | "stems" | "clipAudioUrls"> & { masterKey?: string | null }): SeedDownloadPlan {
  const arr = seed.arrangement as Snapshot | null | undefined;
  const known = seed.clipAudioUrls ?? {};
  const tracks: SeedDownloadTrack[] = [];
  if (arr) {
    const lanes: LaneLike[] = [...(arr.audioTracks ?? []), ...(arr.midiTracks ?? [])];
    for (const lane of lanes) {
      const seen = new Set<string>();
      const files: SeedDownloadTrack["files"] = [];
      const trackName = cleanClipName(lane.name);
      for (const clip of [...(lane.arrangementClips ?? []), ...(lane.sessionClips ?? [])]) {
        const key = clip.audioKey;
        if (!key || seen.has(key) || !known[key]) continue;
        seen.add(key);
        const clipName = clip.name ? cleanClipName(clip.name) : "";
        files.push({ name: clipName || trackName, key });
      }
      if (files.length > 0) tracks.push({ id: `${seed.id}:track:${lane.name}`, name: trackName, files });
    }
  }
  const stemShaped = tracks.length === 0;
  if (stemShaped) {
    // Seeds from before per-clip audio: one stored file per stem.
    for (const st of seed.stems) {
      if (!st.key) continue;
      const name = cleanClipName(st.name);
      tracks.push({ id: `${seed.id}:${st.id}`, name, files: [{ name, key: st.key }] });
    }
  }
  return { title: seed.title, masterKey: seed.masterKey ?? null, tracks, stemShaped };
}

/** One track's files, flat: `<clip>.wav`. */
export function trackFiles(track: SeedDownloadTrack, urlOf: (key: string) => string): DownloadFile[] {
  return uniquePaths(track.files.map((f) => ({ path: `${safeFilename(f.name)}${extOfKey(f.key)}`, url: urlOf(f.key) })));
}

/** The folder everything in a seed's zip sits in. */
export function seedRoot(plan: Pick<SeedDownloadPlan, "title">): string {
  return safeFilename(plan.title);
}

/**
 * The stored part of a seed's zip: the mix, and the clips in one flat folder
 * (named for their track, so twelve "(Freeze)" clips can be told apart). A
 * stem-shaped seed's files go in `arrangement` instead — they are its tracks.
 */
export function seedFiles(plan: SeedDownloadPlan, urlOf: (key: string) => string): DownloadFile[] {
  const root = seedRoot(plan);
  const files: DownloadFile[] = [];
  if (plan.masterKey) files.push({ path: `${root}/${root} (mix)${extOfKey(plan.masterKey)}`, url: urlOf(plan.masterKey) });
  const folder = plan.stemShaped ? "arrangement" : "clips";
  const flat: DownloadFile[] = [];
  for (const track of plan.tracks) {
    const lane = safeFilename(track.name);
    for (const f of track.files) {
      const clip = safeFilename(f.name);
      const name = clip.toLowerCase() === lane.toLowerCase() ? lane : `${lane} - ${clip}`;
      flat.push({ path: `${root}/${folder}/${name}${extOfKey(f.key)}`, url: urlOf(f.key) });
    }
  }
  return [...files, ...uniquePaths(flat)];
}
